//
// accept: set for file input, to restrict file types accepted
// examples:
// "image/*,.pdf"
// ".doc,.docx,.xml,application/msword"
//
// event returned by fileOnInput is:
// event.target.files = [{
//   name,
//   lastModified,
//   lastModifiedDate,
//   size,
// },
// ...]
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useState } from 'react';

import { appTooltipProps } from '../../utils';
import CircularProgress from '../CircularProgress';
import SvgColorImg from '../SvgColorImg';

const Button = function Button(props) {
  const {
    className,
    onClick,
    // description,
    imgSrc,
    Image,
    label,
    disabled,
    id,
    name,
    secondaryColor,
    baseColor,
    criticalColor,
    neutralColor,
    imgRight,
    imgColor,
    fileOnInput,
    absolute,
    noFrame,
    tiny,
    large,
    pending,
    accept,
    multiple,
    submitType,
    imgColorAsCssFill,
    tooltipTxt,
    customImgFillColor = false,
  } = props;
  const [animate, setAnimate] = useState(false);

  // A frameless button with no colour of its own is an icon sitting on the page, and takes
  // the page's interactive colour like a neutral button does
  const neutral = neutralColor || (
    noFrame && (!baseColor) && (!criticalColor) && (!imgColorAsCssFill) && (imgColor === undefined)
  );
  const light = criticalColor || neutralColor;
  const iconColor = imgColor ?? '#FFFFFF';

  const finalClassName = classNames(
    'flex flex-row items-center justify-center gap-2 overflow-hidden shrink-0 text-base',
    {
      'bg-pxMainButtonBase': (!noFrame) && baseColor,
      'bg-white border border-pxMainButtonCritical': (!noFrame) && criticalColor,
      'bg-white border border-pxMainButtonBase': (!noFrame) && neutralColor,
      'text-white': (!neutral) && (!criticalColor),
      'text-pxMainButtonBase': neutral,
      'text-pxMainButtonCritical': criticalColor,
      'fill-white': (!customImgFillColor) && baseColor,
      'fill-pxMainButtonBase': (!customImgFillColor) && neutral,
      'fill-pxMainButtonCritical': (!customImgFillColor) && criticalColor,
      'opacity-50': disabled || pending,
      'shadow-button': (!tiny) && (!light) && (!(disabled || noFrame || pending)),
      'shadow-button-light': (!tiny) && light && (!(disabled || noFrame || pending)),
      'shadow-button-tiny': tiny && (!(disabled || noFrame || pending)),
      'rounded-full': (!label),
      'w-8': ((!label) && (!tiny) && (!large)),
      'w-5': ((!label) && tiny),
      'w-10': ((!label) && large),
      'rounded-lg px-2': label,
      absolute,
      'relative': (!absolute),
      'h-8': !(tiny || large),
      'h-5': tiny,
      'h-10': large,
      // Legacy:
      'bg-pxMainButton2': (!noFrame) && secondaryColor,
      'bg-pxMainButton': (!noFrame) && (!secondaryColor) && (!baseColor) && (!criticalColor) && (!neutralColor),
    },
    className,
  );

  if (fileOnInput) {
    return (
      <label
        // role="button"
        className={finalClassName}
        disabled={disabled || pending}
        htmlFor={id}
        {...appTooltipProps(tooltipTxt)}
      >
        <input
          type="file"
          hidden
          // See https://stackoverflow.com/questions/39484895/how-to-allow-input-type-file-to-select-the-same-file-in-react-component
          // onChange={onInputChange}
          onInput={fileOnInput}
          id={id}
          name={name}
          accept={accept}
          multiple={multiple}
        />

        {(imgSrc)
          ? (
              <SvgColorImg
                src={imgSrc}
                description="."
                className={classNames(
                  'h-6 w-6 shrink-0',
                  {
                    'order-1': !imgRight,
                    'order-3': imgRight,
                  },
                )}
                imgColor={(imgColorAsCssFill || baseColor || criticalColor || neutral) ? undefined : iconColor}
                imgColorAsCssFill={imgColorAsCssFill || baseColor || criticalColor || neutral}
              />
            )
          : null}

        {(Image)
          ? (
              <Image
                description="."
                className={classNames(
                  'h-6 w-6 shrink-0',
                  {
                    'order-1': !imgRight,
                    'order-3': imgRight,
                  },
                )}
                style={
                  (imgColorAsCssFill || baseColor || criticalColor || neutral) ? undefined : { fill: iconColor }
                }
              />
            )
          : null}

        {(label)
          ? <div className="font-medium order-2">{label}</div>
          : null}

        <div
          className={classNames(
            'absolute top-0 left-0 w-full h-full scale-0 opacity-0 bg-white rounded-full border-0',
            {
              'animate-ripple': animate,
            },
          )}
          onAnimationEnd={() => {
          // console.log('end ripple');
            setAnimate(false);
          }}
        />

        {
          (pending)
            ? <div className="absolute top-0 left-0 h-full w-full opacity-60">c</div>
            : null
        }
      </label>
    );
  }

  return (
    <button
      type={submitType ? 'submit' : 'button'}
      className={finalClassName}
      disabled={disabled || pending}
      id={id}
      name={name}
      onClick={(event) => {
        setAnimate(true);
        onClick(event);
      }}
      {...appTooltipProps(tooltipTxt, 'right')}
    >
      {(imgSrc)
        ? (
            <SvgColorImg
              src={imgSrc}
              description="."
              className={classNames(
                {
                  'order-1': !imgRight,
                  'order-3': imgRight,
                  'h-6 w-6': !(tiny || large),
                  'h-4 w-4': tiny,
                  'h-10 w-10': large,
                },
              )}
              imgColor={(imgColorAsCssFill || baseColor || criticalColor || neutral) ? undefined : iconColor}
              imgColorAsCssFill={imgColorAsCssFill || baseColor || criticalColor || neutral}
            />
          )
        : null}

      {(Image)
        ? (
            <Image
              description="."
              className={classNames(
                {
                  'order-1': !imgRight,
                  'order-3': imgRight,
                  'h-6 w-6': !(tiny || large),
                  'h-4 w-4': tiny,
                  'h-10 w-10': large,
                },
              )}
              style={
                (imgColorAsCssFill || baseColor || criticalColor || neutral) ? undefined : { fill: iconColor }
              }
            />
          )
        : null}

      {(label)
        ? <div className="font-normal order-2">{label}</div>
        : null}

      <div
        className={classNames(
          'absolute top-0 left-0 w-full h-full scale-0 opacity-0 bg-white rounded-full border-0 z-10',
          {
            'animate-ripple': animate,
          },
        )}
        onAnimationEnd={() => {
          // console.log('end ripple');
          setAnimate(false);
        }}
      />

      {
        (pending)
          ? (
              <CircularProgress
                withDiv
                full
                white
                className="absolute top-0 left-0 p-0.5"
              />
            )
          : null
      }
    </button>
  );
};

Button.propTypes = {
  className: PropTypes.string,
  onClick: PropTypes.func,
  imgSrc: PropTypes.string,
  Image: PropTypes.elementType,
  label: PropTypes.node,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string,
  secondaryColor: PropTypes.bool,
  baseColor: PropTypes.bool,
  criticalColor: PropTypes.bool,
  neutralColor: PropTypes.bool,
  imgRight: PropTypes.bool,
  imgColor: PropTypes.string,
  fileOnInput: PropTypes.func,
  absolute: PropTypes.bool,
  noFrame: PropTypes.bool,
  tiny: PropTypes.bool,
  large: PropTypes.bool,
  pending: PropTypes.bool,
  accept: PropTypes.string,
  multiple: PropTypes.bool,
  submitType: PropTypes.bool,
  imgColorAsCssFill: PropTypes.bool,
  tooltipTxt: PropTypes.string,
  customImgFillColor: PropTypes.bool,
};

export default Button;
